import { gql } from '@apollo/client';

/**
 * Spec 0011 (survey constructor) — ERP-scope queries. Operation names are
 * deliberately different from the backend field names (`surveys`, `survey`,
 * `groupSurveyAssignments`, …) to avoid the codegen result-type collapse.
 *
 * `questionType` is a plain String on the wire
 * (yes_no|text|rating|single_choice|multi_choice|nps); answer `value` is a
 * JSONString parsed only in the `core/hooks/Surveys` transform layer.
 */

export const SurveysListQuery = gql`
  query ErpSurveysList(
    $courseId: ID
    $includeArchived: Boolean
    $search: String
  ) {
    surveys(
      courseId: $courseId
      includeArchived: $includeArchived
      search: $search
    ) {
      id
      title
      courseId
      courseName
      defaultLessonNumber
      isArchived
      hasResponses
      questionsCount
      assignmentsCount
      updatedAt
    }
  }
`;

export const SurveyDetailQuery = gql`
  query ErpSurveyDetail($id: ID!) {
    survey(id: $id) {
      id
      title
      description
      courseId
      courseName
      defaultLessonNumber
      isArchived
      hasResponses
      questions {
        id
        number
        text
        questionType
        required
        options
      }
    }
  }
`;

export const GroupSurveyAssignmentsQuery = gql`
  query ErpGroupSurveyAssignments($groupId: ID!) {
    groupSurveyAssignments(groupId: $groupId) {
      id
      surveyId
      surveyTitle
      lessonId
      lessonName
      lessonNumber
      lessonDate
      isActive
      origin
      planId
      respondedCount
      eligibleCount
    }
  }
`;

/**
 * Spec 0027 — targeting. `scope` is 'groups' | 'course' and `triggerKind` is
 * 'lesson_number' | 'last_lesson': plain lowercase Strings on the wire, never
 * graphene enums (see SURVEY_PLAN_SCOPES / SURVEY_TRIGGER_KINDS in
 * core/hooks/Surveys/surveyModel.ts).
 */
export const SurveyTargetingQuery = gql`
  query ErpSurveyTargeting($surveyId: ID!) {
    surveyTargeting(surveyId: $surveyId) {
      plans {
        id
        scope
        courseId
        courseName
        onlyActiveGroups
        triggerKind
        triggerLessonNumber
        skipPastLessons
        isActive
        assignmentsCount
        respondedCount
        hasResponses
        groups {
          groupId
          groupName
        }
      }
    }
  }
`;

/**
 * The picker's preview: every candidate group with the lesson the trigger
 * resolves to, or the reason it would be skipped — so «в группе нет занятия
 * №8» is visible before saving instead of arriving in the skip report after.
 */
export const SurveyTargetGroupsQuery = gql`
  query ErpSurveyTargetGroups(
    $surveyId: ID
    $courseId: ID
    $search: String
    $onlyActive: Boolean
    $triggerKind: String
    $triggerLessonNumber: Int
  ) {
    surveyTargetGroups(
      surveyId: $surveyId
      courseId: $courseId
      search: $search
      onlyActive: $onlyActive
      triggerKind: $triggerKind
      triggerLessonNumber: $triggerLessonNumber
    ) {
      groupId
      groupName
      courseId
      courseName
      startDate
      lessonsCount
      isActive
      resolvedLessonId
      resolvedLessonName
      resolvedLessonNumber
      resolvedLessonDate
      resolvedLessonIsOver
      skipReason
      alreadyAttached
    }
  }
`;

/** Pooled results across every group the survey is attached to (spec 0027). */
export const SurveySummaryQuery = gql`
  query ErpSurveySummary(
    $surveyId: ID!
    $groupIds: [ID!]
    $planId: ID
    $dateFrom: Date
    $dateTo: Date
  ) {
    surveySummary(
      surveyId: $surveyId
      groupIds: $groupIds
      planId: $planId
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      survey {
        id
        title
        courseName
      }
      assignmentsCount
      groupsCount
      respondedCount
      eligibleCount
      responseRate
      questionResults {
        question {
          id
          number
          text
          questionType
          required
          options
        }
        answersCount
        average
        distribution {
          key
          label
          count
        }
      }
      groupBreakdown {
        assignmentId
        groupId
        groupName
        lessonName
        lessonNumber
        lessonDate
        isActive
        origin
        respondedCount
        eligibleCount
        responseRate
        averageRating
      }
    }
  }
`;

/** One teacher's survey feedback (spec 0027) — the card's «Отзывы» tab. */
export const TeacherSurveyFeedbackQuery = gql`
  query ErpTeacherSurveyFeedback(
    $teacherId: ID!
    $courseId: ID
    $groupId: ID
    $dateFrom: Date
    $dateTo: Date
    $bucket: String
  ) {
    teacherSurveyFeedback(
      teacherId: $teacherId
      courseId: $courseId
      groupId: $groupId
      dateFrom: $dateFrom
      dateTo: $dateTo
      bucket: $bucket
    ) {
      teacherId
      teacherName
      average
      ratingsCount
      commentsCount
      trend {
        period
        average
        count
      }
      byCourse {
        id
        name
        average
        count
      }
      byGroup {
        id
        name
        average
        count
      }
      comments {
        studentName
        comment
        createdAt
        courseName
        groupName
      }
    }
  }
`;

/** Cross-teacher table, weakest first (spec 0027). */
export const TeacherSurveyLeaderboardQuery = gql`
  query ErpTeacherSurveyLeaderboard(
    $courseId: ID
    $dateFrom: Date
    $dateTo: Date
    $minResponses: Int
  ) {
    teacherSurveyLeaderboard(
      courseId: $courseId
      dateFrom: $dateFrom
      dateTo: $dateTo
      minResponses: $minResponses
    ) {
      teacherId
      teacherName
      average
      ratingsCount
      commentsCount
      coursesCount
      enoughData
    }
  }
`;

export const SurveyAssignmentResultsQuery = gql`
  query ErpSurveyAssignmentResults($assignmentId: ID!) {
    surveyAssignmentResults(assignmentId: $assignmentId) {
      assignment {
        id
        surveyTitle
        groupId
        groupName
        lessonName
        lessonNumber
        respondedCount
        eligibleCount
      }
      respondents {
        studentId
        fullName
        email
        responded
        submittedAt
        responseId
      }
      questionResults {
        question {
          id
          number
          text
          questionType
          required
          options
        }
        answersCount
        average
        distribution {
          key
          label
          count
        }
      }
    }
  }
`;

export const SurveyResponseDetailQuery = gql`
  query ErpSurveyResponseDetail($responseId: ID!) {
    surveyResponseDetail(responseId: $responseId) {
      responseId
      studentId
      fullName
      submittedAt
      answers {
        questionId
        value
      }
    }
  }
`;
